import React, { useEffect, useState, lazy, Suspense, Fragment } from "react";
import PropTypes from "prop-types";
import ExecutionEnvironment from "exenv";
// Components
import CustomMeta from "../components/CustomMeta/CustomMeta";
import config from "../utils/config";

const LoginOrSignup = lazy(() =>
  import(
    /* webpackChunkName: "LoginOrSignup" */ "../components/Login/LoginOrSignup"
  )
);
const SetAffiliateDetailsForm = lazy(() =>
  import(
    /* webpackChunkName: "SetAffiliateDetailsForm" */ "../components/Affiliate/SetAffiliateDetailsForm"
  )
);
const AffiliateDetails = lazy(() =>
  import(
    /* webpackChunkName: "AffiliateDetails" */ "../components/Affiliate/AffiliateDetails"
  )
);
const AffiliateStats = lazy(() =>
  import(
    /* webpackChunkName: "AffiliateStats" */ "../components/Affiliate/AffiliateStats"
  )
);
const AffiliateSales = lazy(() =>
  import(
    /* webpackChunkName: "AffiliateSales" */ "../components/Affiliate/AffiliateSales"
  )
);
const AffiliatePayouts = lazy(() =>
  import(
    /* webpackChunkName: "AffiliatePayouts" */ "../components/Affiliate/AffiliatePayouts"
  )
);
const AffiliateTerms = lazy(() =>
  import(
    /* webpackChunkName: "AffiliateTerms" */ "../components/Affiliate/AffiliateTerms"
  )
);

import { makeStyles } from "tss-react/mui";
import { Typography, CircularProgress, Grid, Paper } from "@mui/material";
import Link from "@mui/material/Link";

// redux stuff
import { connect } from "react-redux";
import { getAffiliateData } from "../redux/actions/dataActions";

const useStyles = makeStyles()((theme) => ({
  fullPageHeight: {
    minHeight: "100vh",
    //width: "100%",
  },
  heading: {
    paddingTop: theme.spacing(2),
  },
  subTitle: {
    padding: "0px 30px 0px 30px",
    textAlign: "center",
    fontSize: 20,
  },
  loadingDiv: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  gridContainer: {
    display: "flex",
    justifyContent: "center",
    padding: `0 ${theme.spacing(2)} ${theme.spacing(2)} ${theme.spacing(2)}`,
    // Grid item
    "& .MuiGrid-item": {
      display: "flex",
      flexDirection: "column",
    },
    "& .MuiGrid-spacing-xs-2": {
      margin: 0,
      width: "100%",
    },
  },
  paper: {
    padding: theme.spacing(2),
    flexGrow: 1,
  },
  gridRoot: {
    flexGrow: 1,
    padding: theme.spacing(1),
    maxWidth: 1237,
  },
  list: {
    "& li": {
      marginBottom: theme.spacing(2),
    },
  },
  question: {
    fontWeight: "bold",
  },
  answer: {
    marginBottom: theme.spacing(2),
  },
}));

function affiliate(props) {
  const { classes } = useStyles();

  useEffect(() => {
    props.getAffiliateData();
  }, [props.user.authenticated]);

  function getAffiliateMarkup() {
    if (props.dataLoading || props.userLoading) {
      return (
        <div className={classes.loadingDiv}>
          <CircularProgress size={25} />
        </div>
      );
    } else if (
      props.user.authenticated &&
      typeof props.data.affiliateData.affiliateProfile != "undefined"
    ) {
      return (
        <Grid
          container
          className={classes.gridRoot}
          spacing={2}
          direction="row"
          justifyContent="center"
        >
          <Grid item xs={12} sm={12} md={6}>
            <AffiliateDetails />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <AffiliateStats />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <AffiliateSales />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <AffiliatePayouts />
          </Grid>
          {affiliateInfoMarkup}
        </Grid>
      );
    } else {
      return (
        <Fragment>
          <Grid
            container
            className={classes.root}
            spacing={2}
            direction="row"
            justifyContent="center"
          >
            <Grid item xs={12}>
              {props.user.authenticated ? (
                <SetAffiliateDetailsForm />
              ) : (
                <LoginOrSignup
                  titleText={"Login or sign up to create an affiliate account"}
                />
              )}
            </Grid>
            {affiliateInfoMarkup}
          </Grid>
        </Fragment>
      );
    }
  }

  let affiliateInfoMarkup = (
    <Fragment>
      <Grid item xs={12}>
        <Typography variant="h2" align="left">
          How to get started
        </Typography>
        <Typography>
          It’s really simple! Use your preferred media channel to talk about
          Post Generator (Youtube, blog, newsletter, Pinterest, Facebook,
          Instagram,...) and get paid for any client you refer.
        </Typography>
        <br />
        <Typography variant="h2" align="left">
          (Y)our Audience
        </Typography>
        <Typography>
          Promoting Content Generator will work best if your audience overlaps
          with our audience… If you’re talking to one of the following groups of
          people, there will be a great synergy:
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4} md={4}>
        <Paper className={classes.paper}>
          <Typography variant="h3">🌐 Bloggers</Typography>
          <Typography>
            Bloggers are always looking for ways to diversify their traffic.
            With Content Generator, they can put their social media marketing
            efforts on autopilot and start seeing real results.
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={4} md={4}>
        <Paper className={classes.paper}>
          <Typography variant="h3">🛍️ Ecommerce</Typography>
          <Typography>
            Content Generator offers a direct integration with Etsy and Shopify
            which makes it the perfect companion for ecommerce businesses who
            are looking for a social media marketing automation tool.
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={4} md={4}>
        <Paper className={classes.paper}>
          <Typography variant="h3">💸 Affiliate Marketers</Typography>
          <Typography>
            Content Generator allows for easy bulk creation of posts which is
            exactly what this target audience needs.
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h2" align="left">
          Where We Shine
        </Typography>
        <Typography>
          The biggest benefits of using Content Generator are:
        </Typography>
        <ul className={classes.list}>
          <li>
            <b>⏱️ Save time</b> - Our customers save hours in post creation and
            scheduling using Content Generator instead of non-specific tools
            like Canva.
          </li>
          <li>
            <b>📈 Consistency</b> - Because of the effort necessary to create
            and schedule posts, many site owners give up before seeing success
            or never actually create the volume of fresh posts needed to get
            significant results from social media. Content Generator allows them
            to finally create and schedule enough posts to reap the rewards.
          </li>
          <li>
            <b>✍🏻 Beat Writer's Block With AI</b> - Content Generator has an AI
            writer integrated within the tool. This allows the user to
            automatically rewrite titles and post descriptions. Creating truly
            unique posts has never been so easy.
          </li>
          <li>
            <b>🎨 Designs That Match</b> - On top of being able to choose from
            100+ pre-made designs, customers can create their very own post
            templates. This means that the design of the posts can fully match
            the website esthetics.
          </li>
          <li>
            <b>🤖 Next Level Content Creation Automation</b> - Content Generator
            has a feature that no other tool offers… It's called "AutoPosts" and
            it allows for set and forget post creation. Imagine that every month
            a completely new post gets created and posted. That's the power of
            AutoPosts!
          </li>
          <li>
            <b>⌛ One Click Scheduling</b> - Content Generator has a scheduler
            that allows to automatically post a set number of posts per day.
            Scheduling hundreds of posts really is one click easy!
          </li>
        </ul>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h2" align="left">
          Affiliate Program FAQ
        </Typography>
        <Typography className={classes.question}>
          Where do I sign up for the affiliate program?
        </Typography>
        <Typography className={classes.answer}>
          Sign up at the top of this page.
        </Typography>
        <Typography className={classes.question}>
          Do I need an account to be able to promote Content Generator?
        </Typography>
        <Typography className={classes.answer}>
          No, you can become an affiliate without a paid Content Generator
          account, however our best affiliates are those who know and use the
          tool and can speak with authority about it’s features and benefits!
        </Typography>
        <Typography className={classes.question}>
          What’s the cookie duration?
        </Typography>
        <Typography className={classes.answer}>
          The affiliate cookie is 30 days
        </Typography>
        <Typography className={classes.question}>
          What’s the payment payout threshold?
        </Typography>
        <Typography className={classes.answer}>
          The minimum payout is $100
        </Typography>
        <Typography className={classes.question}>
          When will I get paid?
        </Typography>
        <Typography className={classes.answer}>
          Payments are processed on or before the 5th day of the month.Payments
          are held for 1 calendar month after the sale to accommodate refunds.
        </Typography>
        <Typography className={classes.question}>
          I have a special request, who do I contact?
        </Typography>
        <Typography className={classes.answer}>
          You can reach out to info at contentgenerator dot io with any
          suggestions or questions about promoting Content Generator.
        </Typography>
        <Typography>
          You can find the full affiliate agreement here{" "}
          <Link
            href={`https://firebasestorage.googleapis.com/v0/b/${config.prod.storageBucket}/o/affiliate-agreement.pdf?alt=media`}
            target="_blank"
            underline="hover"
          >
            here.
          </Link>
        </Typography>
      </Grid>
    </Fragment>
  );

  return (
    <div className={classes.fullPageHeight}>
      <CustomMeta
        title={`Affiliate Program for Content Generator - Earn 25% Commission`}
        description={`Join Content Generator's affiliate program and earn 25% commission promoting the #1 Content marketing automation tool. Perfect for bloggers, e-commerce stores, and affiliate marketers. Get paid for recurring sales and help others grow their Pinterest presence.`}
        url={`https://contentgenerator.io/affiliate`}
      />
      <Typography variant="h1" className={classes.heading}>
        Get Paid To Share Content Generator
      </Typography>
      <Typography className={classes.subTitle}>
        Become a Content Generator affiliate today and get{" "}
        <b>25% commission on qualifying sales</b> including recurring charges.
      </Typography>
      <div className={classes.gridContainer}>
        {ExecutionEnvironment.canUseDOM && (
          <Suspense fallback={<div />}>{getAffiliateMarkup()}</Suspense>
        )}
      </div>
    </div>
  );
}

affiliate.propTypes = {
  data: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  data: state.data,
  user: state.user,
  UI: state.UI,
  dataLoading: state.data.loading,
  userLoading: state.user.loading,
});

export default connect(mapStateToProps, { getAffiliateData })(affiliate);
